import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
  MessageBar,
  TextInput,
  useHasPermission,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dialog, makeStyles } from '@material-ui/core';
import { faCircleUser, faInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Role } from '@/types/user';
import { UserCreatePayload } from '../types';
import { isRequired, isValidEmail, isValidName } from '../validators';
import { CustomTooltip } from '../../../../shared_elements';
import { userCreateRoleTooltipTemplate } from '../constants';
import { roleEnumToLabel, toggleRole } from '../utils';
import { Checkbox } from '@/components';

interface UserCreateModalProps {
  open: boolean;
  disabled: boolean;
  roles: string[];
  onClose: () => void;
  onSubmit: (data: UserCreatePayload) => void;
}

const useStyles = makeStyles({
  container: {
    width: '525px',
    padding: '24px 32px 9px',
  },
});

const UserCreateModal: FunctionComponent<UserCreateModalProps> = ({
  open,
  disabled,
  roles,
  onClose,
  onSubmit,
}) => {
  const classes = useStyles();
  const canAssignFinance = useHasPermission('profile.assignFinance') as boolean;

  const { handleSubmit, control, reset } = useForm<UserCreatePayload>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      userAppRole: '',
      secondaryUserAppRoles: [],
    },
  });
  const options = useMemo(
    () =>
      roles.map((role) => ({
        label: roleEnumToLabel(role),
        value: role,
      })),
    [roles]
  );
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  return (
    <Dialog open={open} classes={{ paper: classes.container }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Flex
          sx={{
            flexDirection: 'column',
            rowGap: '8px',
            color: '#333E41',
            fontFamily: 'Roboto',
          }}
          data-testid="userCreateModal"
        >
          <Flex
            sx={{
              flexDirection: 'row',
              justifyContent: 'center',
              columnGap: '12px',
              padding: '12px 0',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '28px',
            }}
          >
            <FontAwesomeIcon icon={faCircleUser} />
            <Box>Add User</Box>
          </Flex>
          <Box
            sx={{
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '12px',
            }}
          >
            * Indicates a Required Field
          </Box>
          <Flex sx={{ flexDirection: 'column', rowGap: '20px' }}>
            <Controller
              name="firstName"
              control={control}
              rules={{
                validate: {
                  isRequired,
                  isValidName,
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error = {} },
              }) => (
                <TextInput
                  required
                  disabled={disabled}
                  error={!!error.message}
                  label="First Name"
                  note={error.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="firstName"
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                validate: {
                  isRequired,
                  isValidName,
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error = {} },
              }) => (
                <TextInput
                  required
                  disabled={disabled}
                  error={!!error.message}
                  label="Last Name"
                  note={error.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="lastName"
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                validate: {
                  isRequired,
                  isValidEmail,
                },
              }}
              render={({
                field: { value, onChange, onBlur },
                fieldState: { error = {} },
              }) => (
                <TextInput
                  required
                  disabled={disabled}
                  error={!!error.message}
                  label="Email"
                  note={error.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="email"
                />
              )}
            />
            <Flex
              sx={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                columnGap: '8px',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="userAppRole"
                  control={control}
                  rules={{
                    validate: {
                      isRequired,
                    },
                  }}
                  render={({
                    field: { value, onChange, onBlur },
                    fieldState: { error = {} },
                  }) => (
                    <Dropdown
                      required
                      disabled={disabled}
                      error={!!error.message}
                      label="Role"
                      helperText={error.message}
                      options={options}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      data-testid="role"
                    />
                  )}
                />
              </Box>
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                }}
              >
                <CustomTooltip
                  arrow
                  placement="right"
                  title={userCreateRoleTooltipTemplate}
                  style={{ color: '#0091B3' }}
                >
                  <FontAwesomeIcon icon={faInfo} />
                </CustomTooltip>
              </Flex>
            </Flex>
          </Flex>
          <Box
            sx={{ height: 1, background: '#CCCFD0', width: '100%', my: 16 }}
          />
          <Flex sx={{ flexDirection: 'column', gap: 8 }}>
            <Box sx={{ padding: '7px 4px' }}>
              <Controller
                name="secondaryUserAppRoles"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    label="Finance Permission"
                    disabled={!canAssignFinance}
                    checked={(value || []).includes(Role.Finance)}
                    onChange={(checked: boolean) =>
                      onChange(toggleRole(value || [], Role.Finance, checked))
                    }
                    data-testid="financePermissionCheckbox"
                  />
                )}
              />
            </Box>
            <Flex>
              <Box sx={{ width: 28 }} />
              <MessageBar
                type="info"
                title="Grants a user access to view and download detailed billing records. Only users with a Manager role can assign this permission."
                showClose={false}
                sx={{
                  '& h4': {
                    fontWeight: 400,
                    color: '#4D5659',
                  },
                }}
              />
            </Flex>
          </Flex>
          <Flex
            sx={{
              flexDirection: 'row',
              justifyContent: 'center',
              columnGap: '20px',
              padding: '30px 0 40px',
            }}
          >
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="cancelButton"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={disabled} data-testid="saveButton">
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Dialog>
  );
};

export default UserCreateModal;
